import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { motion } from "motion/react";
import { useState } from "react";
import { Sun, Moon, LogOut, Search, Building2, Menu, CodeXml, Bell } from "lucide-react";
import { useTheme } from "./ThemeProvider";
import { useAuth } from "../auth/AuthContext";
import { useCommandMenu } from "./CommandMenu";
import { useAgentArea } from "@/hooks/useAgentArea";
import { Button } from "./ui/button";
import { Kbd } from "./ui/kbd";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { Avatar, AvatarFallback } from "./ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from "./ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "./ui/sheet";
import { Link, useNavigate } from "react-router-dom";
import { useBrand } from "@/branding/BrandProvider";
import { SidebarContent } from "./Sidebar";
import { OrgSwitcher } from "./OrgSwitcher";
import { ClientTypePill } from "./ClientTypePill";
import { useWhatsAppEnabled } from "@/hooks/useWhatsAppEnabled";

function getInitials(name: string | null | undefined, email: string): string {
  if (name && name.trim()) {
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.charAt(0) ?? "";
    const last = parts.length > 1 ? parts[parts.length - 1]!.charAt(0) : "";
    return (first + last).toUpperCase();
  }
  return email.charAt(0).toUpperCase();
}

/** `bare`: no search or nav menu, for pages outside the app's chrome (the demo's /try while it builds). */
export function Header({ bare = false }: { bare?: boolean }) {
  const reducedMotion = usePrefersReducedMotion();
  const { theme, toggleTheme } = useTheme();
  const brand = useBrand();
  const logo = theme === "dark" ? brand.logoDark : brand.logoLight;
  const { user, isAuthenticated, logout, activeOrganisationId, devPersona } = useAuth();
  const { setOpen: setCommandOpen } = useCommandMenu();
  const { label: areaLabel } = useAgentArea();
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // The account's only personal setting so far is WhatsApp notifications, so the item follows the module.
  const whatsAppEnabled = useWhatsAppEnabled();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const hasMultipleOrgs = (user?.organisations?.length ?? 0) > 1;
  const activeOrgName =
    user?.organisations.find((o) => o.organisationId === activeOrganisationId)?.organisationName ??
    null;

  return (
    <header className="sticky top-0 z-50 flex h-14 w-full shrink-0 items-center justify-between gap-2 border-b bg-background/95 px-3 backdrop-blur supports-[backdrop-filter]:bg-background/60 sm:px-4">
      <div className="flex min-w-0 items-center gap-2 sm:gap-3">
        {isAuthenticated && !bare && (
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-9 md:hidden"
                aria-label="Open navigation menu"
              >
                <Menu className="size-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[260px] p-0 sm:max-w-[260px]" showCloseButton={false}>
              <SheetHeader className="sr-only">
                <SheetTitle>Navigation</SheetTitle>
                <SheetDescription>Browse agents and organisation settings.</SheetDescription>
              </SheetHeader>
              <SidebarContent onNavigate={() => setMobileNavOpen(false)} />
            </SheetContent>
          </Sheet>
        )}

        <Link to={isAuthenticated ? "/" : "https://dolfinai.co"} className="flex shrink-0 items-center gap-2 font-semibold">
          <img src={logo} alt={brand.name} className="h-7 w-7" />
          <span className="hidden sm:inline">{brand.name}</span>
        </Link>

        {isAuthenticated && areaLabel && (
          <div className="hidden items-center gap-2 text-sm sm:flex">
            <span className="text-muted-foreground">/</span>
            <span className="text-muted-foreground">{areaLabel}</span>
          </div>
        )}
      </div>

      {isAuthenticated && !bare && (
        <motion.button
          type="button"
          whileTap={reducedMotion ? undefined : { scale: 0.98 }}
          onClick={() => setCommandOpen(true)}
          className="hidden h-9 w-[360px] items-center gap-2 rounded-full border bg-background px-3 text-sm text-muted-foreground transition hover:bg-muted md:flex"
        >
          <Search className="size-4" />
          <span className="flex-1 truncate text-left">Ask agent or jump to…</span>
          <Kbd>&#8984;K</Kbd>
        </motion.button>
      )}

      <div className="flex items-center gap-1.5 sm:gap-2">
        {isAuthenticated && <ClientTypePill />}
        {isAuthenticated && hasMultipleOrgs && <OrgSwitcher />}

        {isAuthenticated && devPersona ? (
          // Dev-login persona badge; mirrors @dolfin/ui DevModeBadge, which sandbox doesn't depend on.
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                data-testid="dev-login-indicator"
                className="inline-flex h-9 max-w-64 items-center gap-1.5 rounded-md border border-amber-500/50 bg-amber-500/10 px-2.5 text-xs font-medium text-amber-700 dark:text-amber-400"
              >
                <CodeXml className="size-3.5 shrink-0" />
                <span className="font-semibold uppercase tracking-wide">Dev</span>
                <span className="hidden truncate md:inline">{devPersona.label}</span>
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              Dev login · {devPersona.label}
            </TooltipContent>
          </Tooltip>
        ) : null}

        <Button
          variant="outline"
          size="icon"
          className="size-9"
          onClick={toggleTheme}
          aria-label="Toggle theme"
        >
          {theme === "dark" ? (
            <Sun className="h-4 w-4" />
          ) : (
            <Moon className="h-4 w-4" />
          )}
        </Button>

        {isAuthenticated && user ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="grid size-9 place-items-center rounded-full ring-offset-background transition hover:ring-2 hover:ring-accent-blue/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue"
                aria-label="Account menu"
              >
                <Avatar className="size-8">
                  <AvatarFallback className="bg-brand text-xs font-semibold text-brand-foreground">
                    {getInitials(user.name, user.email)}
                  </AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[220px]">
              <div className="px-2 py-1.5">
                {user.name && <p className="text-sm font-medium">{user.name}</p>}
                <p className="text-xs text-muted-foreground">{user.email}</p>
              </div>
              <DropdownMenuSeparator />
              {whatsAppEnabled ? (
                <>
                  <DropdownMenuItem onClick={() => navigate("/account/notifications")}>
                    <Bell className="mr-2 h-4 w-4" />
                    Notifications
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              ) : null}
              <DropdownMenuLabel className="flex items-center gap-2 text-xs font-normal text-muted-foreground">
                <Building2 className="h-3.5 w-3.5" />
                {activeOrgName ?? "Organisation"}
              </DropdownMenuLabel>
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
    </header>
  );
}
